import { useNavigate } from 'react-router-dom';
import { Compass, LogOut, Menu, UserCircle2 } from 'lucide-react';
import { useAuth } from '@/features/auth/AuthProvider';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { StoreSwitcher } from './StoreSwitcher';
import { NotificationsBell } from './NotificationsBell';
import { useSidebarUI } from './sidebarUI';

type TopbarProps = {
  onGuidedNavigationClick: () => void;
};

export function Topbar({ onGuidedNavigationClick }: TopbarProps) {
  const { user, role, displayName, signOut } = useAuth();
  const navigate = useNavigate();
  const { setMobileOpen } = useSidebarUI();

  return (
    <header className="sticky top-0 z-30 flex h-14 items-center justify-between gap-2 border-b bg-background/80 px-3 backdrop-blur sm:h-16 sm:gap-3 sm:px-6">
      <div className="flex items-center gap-2 sm:gap-3">
        {/* Hamburger — mobile only */}
        <Button
          variant="ghost"
          size="icon"
          className="md:hidden"
          onClick={() => setMobileOpen(true)}
          aria-label="Open menu"
        >
          <Menu className="h-5 w-5" />
        </Button>
        <StoreSwitcher />
      </div>

      <div className="flex items-center gap-1 sm:gap-2">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="hidden sm:inline-flex"
          onClick={onGuidedNavigationClick}
        >
          <Compass className="h-4 w-4" />
          Guided Navigation
        </Button>
        {role && (
          <Badge
            variant={role === 'owner' ? 'gold' : role === 'manager' ? 'secondary' : 'muted'}
            className="hidden capitalize sm:inline-flex"
          >
            {role}
          </Badge>
        )}
        <NotificationsBell />
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon">
              <UserCircle2 className="h-5 w-5" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuLabel className="text-xs font-normal text-muted-foreground">
              <div className="font-medium text-foreground">{displayName || 'User'}</div>
              {user?.email && <div className="text-[11px]">{user.email}</div>}
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={async () => {
                await signOut();
                navigate('/login');
              }}
            >
              <LogOut className="h-4 w-4" /> Sign out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
}
